import React, { useState, useRef } from 'react';
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const SchemaValidatorPage = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [schemaInput, setSchemaInput] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState('');
  const [schemaVersion, setSchemaVersion] = useState('draft-07');
  const [copySuccess, setCopySuccess] = useState(false);
  const jsonFileInputRef = useRef(null);
  const schemaFileInputRef = useRef(null);

  // Example JSON for testing
  const exampleJson = {
    person: {
      name: "John Doe",
      age: 30,
      email: "john@example.com",
      isEmployed: true,
      address: {
        street: "123 Main St",
        city: "Anytown",
        zipCode: "12345"
      },
      phoneNumbers: [
        {
          type: "home",
          number: "555-1234"
        },
        {
          type: "work",
          number: "555-5678"
        }
      ]
    }
  };

  // Example Schema for testing
  const exampleSchema = {
    "$schema": "http://json-schema.org/draft-07/schema#",
    "type": "object",
    "properties": {
      "person": {
        "type": "object",
        "required": ["name", "age", "email"],
        "properties": {
          "name": {
            "type": "string",
            "minLength": 2
          },
          "age": {
            "type": "integer",
            "minimum": 0,
            "maximum": 120
          },
          "email": {
            "type": "string",
            "format": "email"
          },
          "isEmployed": {
            "type": "boolean"
          },
          "address": {
            "type": "object",
            "properties": {
              "street": {
                "type": "string"
              },
              "city": {
                "type": "string"
              },
              "zipCode": {
                "type": "string",
                "pattern": "^\\d{5}$"
              }
            }
          },
          "phoneNumbers": {
            "type": "array",
            "items": {
              "type": "object",
              "required": ["type", "number"],
              "properties": {
                "type": {
                  "type": "string",
                  "enum": ["home", "work", "mobile"]
                },
                "number": {
                  "type": "string",
                  "pattern": "^\\d{3}-\\d{4}$"
                }
              }
            }
          }
        }
      }
    }
  };

  // Schema draft versions
  const schemaVersions = [
    { id: 'draft-07', name: 'Draft-07' },
    { id: 'draft-06', name: 'Draft-06' },
    { id: 'draft-04', name: 'Draft-04' },
    { id: 'draft-03', name: 'Draft-03' }
  ];

  // Handle JSON validation against schema using Ajv
  const validateJson = () => {
    try {
      setError('');
      setValidationResult(null);
      
      // Parse the JSON input
      if (!jsonInput.trim()) {
        setError('Please enter JSON data');
        return;
      }
      
      if (!schemaInput.trim()) {
        setError('Please enter a JSON Schema');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const parsedSchema = JSON.parse(schemaInput);
      
      // Create Ajv instance based on selected schema version
      let ajvOptions = {};
      
      if (schemaVersion === 'draft-07') {
        ajvOptions = { strict: false };
      } else if (schemaVersion === 'draft-06') {
        ajvOptions = { schemaId: 'id', strict: false };
      } else if (schemaVersion === 'draft-04') {
        ajvOptions = { schemaId: 'id', strict: false };
      } else if (schemaVersion === 'draft-03') {
        ajvOptions = { schemaId: 'id', strict: false };
      }
      
      const ajv = new Ajv(ajvOptions);
      addFormats(ajv);
      
      try {
        const validate = ajv.compile(parsedSchema);
        const valid = validate(parsedJson);
        
        if (valid) {
          setValidationResult({
            valid: true,
            message: 'JSON is valid against the provided schema',
            errors: []
          });
        } else {
          setValidationResult({
            valid: false,
            message: 'JSON validation failed',
            errors: validate.errors.map(err => ({
              message: ajv.errorsText([err], { dataVar: '' }).trim(),
              path: err.instancePath || err.dataPath
            }))
          });
        }
      } catch (schemaError) {
        setError('Schema validation error: ' + schemaError.message);
      }
    } catch (e) {
      setError('Error during validation: ' + e.message);
    }
  };

  const handleJsonFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setJsonInput(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleSchemaFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSchemaInput(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleJsonUploadClick = () => {
    jsonFileInputRef.current.click();
  };

  const handleSchemaUploadClick = () => {
    schemaFileInputRef.current.click();
  };

  const loadJsonExample = () => {
    setJsonInput(JSON.stringify(exampleJson, null, 2));
  };

  const loadSchemaExample = () => {
    setSchemaInput(JSON.stringify(exampleSchema, null, 2));
  };

  const copyToClipboard = () => {
    if (!validationResult) {
      setError('Nothing to copy');
      return;
    }
    
    try {
      const textToCopy = JSON.stringify(validationResult, null, 2);
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(err => {
          setError('Failed to copy: ' + err.message);
        });
    } catch (e) {
      setError('Error copying to clipboard: ' + e.message);
    }
  };

  const getValidationResultText = () => {
    if (error) {
      return error;
    }
    
    if (!validationResult) {
      return "Enter JSON data and schema, then click \"Validate\"";
    }
    
    if (validationResult.valid) {
      return "✓ JSON is valid against the provided schema";
    }
    
    let resultText = "✗ JSON validation failed\n\n";
    resultText += "Validation Errors:\n";
    
    validationResult.errors.forEach((err, index) => {
      resultText += `• ${err.message}\n`;
      if (err.path) {
        resultText += `  at: ${err.path}\n`;
      }
      if (index < validationResult.errors.length - 1) {
        resultText += "\n";
      }
    });
    
    return resultText;
  };

  return (
    <>
      <SEO 
        title="JSON Schema Validator" 
        description="Free online JSON Schema Validator. Validate your JSON data against JSON Schema. Supports Draft-07, Draft-06, Draft-04, and Draft-03."
        keywords={["JSON schema validator", "validate JSON", "JSON schema", "schema validation"]}
        canonicalUrl="https://yourjsontools.com/schema-validator"
      />
      
      {/* AdSense top ad - after navbar, before content */}
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '90%',
        maxWidth: '1600px',
        minHeight: 'calc(100vh - 150px)',
        margin: '0 auto',
        padding: '20px'
      }}>
        <h1 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          margin: '0 0 20px 0',
          color: '#333'
        }}>
          JSON Schema Validator
        </h1>
        
        {/* Top section with input areas side by side */}
        <div className="responsive-main-container" style={{ 
          display: 'flex', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          {/* Left panel - JSON Input */}
          <div className="responsive-panel" style={{ 
            flex: 1,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px 20px',
              borderBottom: '1px solid #eee'
            }}>
              <div style={{ fontWeight: '500', color: '#333' }}>JSON Data</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={loadJsonExample}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Example
                </button>
                <input
                  type="file"
                  ref={jsonFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleJsonFileUpload}
                  accept=".json"
                />
                <button
                  onClick={handleJsonUploadClick}
                  style={{
                    backgroundColor: '#26A69A',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Upload
                </button>
              </div>
            </div>
            
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              style={{
                flex: 1,
                padding: '15px',
                border: 'none',
                resize: 'none',
                fontFamily: 'monospace',
                fontSize: '14px',
                minHeight: '600px',
                outline: 'none'
              }}
              placeholder={"Enter your JSON data here..."}
            />
          </div>

          {/* Right panel - Schema Input */}
          <div className="responsive-panel" style={{ 
            flex: 1,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px 20px',
              borderBottom: '1px solid #eee'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px' 
              }}>
                <div style={{ fontWeight: '500', color: '#333' }}>JSON Schema</div>
                <select
                  value={schemaVersion}
                  onChange={(e) => setSchemaVersion(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontSize: '14px'
                  }}
                >
                  {schemaVersions.map(version => (
                    <option key={version.id} value={version.id}>
                      {version.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={loadSchemaExample}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Example
                </button>
                <input
                  type="file"
                  ref={schemaFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleSchemaFileUpload}
                  accept=".json"
                />
                <button
                  onClick={handleSchemaUploadClick}
                  style={{
                    backgroundColor: '#26A69A',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Upload
                </button>
              </div>
            </div>
            
            <textarea
              value={schemaInput}
              onChange={(e) => setSchemaInput(e.target.value)}
              style={{
                flex: 1,
                padding: '15px',
                border: 'none',
                resize: 'none',
                fontFamily: 'monospace',
                fontSize: '14px',
                minHeight: '600px',
                outline: 'none'
              }}
              placeholder={"Enter your JSON Schema here..."}
            />
          </div>
        </div>

        {/* Bottom section - Validation Results */}
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: '1px solid #eee'
          }}>
            <div style={{ fontWeight: '500', color: '#333' }}>Validation Results</div>
            <button
              onClick={validateJson}
              style={{
                backgroundColor: '#1976D2',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Validate
            </button>
          </div>
          
          <textarea
            readOnly
            value={getValidationResultText()}
            style={{
              padding: '15px',
              border: 'none',
              resize: 'none',
              fontFamily: 'monospace',
              fontSize: '14px',
              minHeight: '200px',
              outline: 'none',
              backgroundColor: !validationResult && !error ? 'white' : 
                               error ? '#FFEBEE' : 
                               validationResult && validationResult.valid ? '#E8F5E9' : '#FFEBEE',
              color: !validationResult && !error ? '#333' :
                     error ? '#C62828' : 
                     validationResult && validationResult.valid ? '#2E7D32' : '#C62828'
            }}
          />
        </div>
      </div>
      
      {/* SEO Content Section */}
      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About JSON Schema Validator</h2>
        <p style={{ marginBottom: '15px' }}>
          JSON Schema validation is an essential process for ensuring that your JSON data adheres to a specific structure and format. By validating against a schema, you can catch errors early and maintain data consistency throughout your application.
        </p>
        <p style={{ marginBottom: '15px' }}>
          Our <strong>JSON Schema Validator</strong> provides a simple yet powerful way to validate your JSON data against JSON Schema standards. This is particularly valuable when:
        </p>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Testing API request and response payloads</li>
          <li style={{ marginBottom: '8px' }}>Verifying user-submitted data before processing</li>
          <li style={{ marginBottom: '8px' }}>Ensuring configuration files meet requirements</li>
          <li style={{ marginBottom: '8px' }}>Developing with JSON-based databases</li>
          <li style={{ marginBottom: '8px' }}>Implementing contract-based development for APIs</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Key Features</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Multiple Schema Versions</strong>: Support for JSON Schema drafts 07, 06, 04, and 03</li>
          <li style={{ marginBottom: '8px' }}><strong>Detailed Error Messages</strong>: Clear indications of where and why validation fails</li>
          <li style={{ marginBottom: '8px' }}><strong>Format Validation</strong>: Checks for email, date, URI, and other special formats</li>
          <li style={{ marginBottom: '8px' }}><strong>Example Data</strong>: Built-in examples to help you get started quickly</li>
          <li style={{ marginBottom: '8px' }}><strong>File Upload</strong>: Upload your JSON data and schema files directly</li>
          <li style={{ marginBottom: '8px' }}><strong>Visual Feedback</strong>: Color-coded results make it easy to see validation status</li>
        </ul>
        <p style={{ marginBottom: '15px' }}>
          All validation occurs directly in your browser, so your sensitive data never leaves your device. This makes our tool suitable for validating private or confidential information.
        </p>
        <p>
          Whether you're an API developer ensuring data quality, a front-end developer validating form submissions, or a QA engineer testing data flows, our JSON Schema Validator provides a fast, reliable way to verify your JSON data structures against defined schemas.
        </p>
      </div>
      
      {/* AdSense bottom ad - after content */}
      
    </>
  );
};

export default SchemaValidatorPage; 